/* eslint-disable @typescript-eslint/no-explicit-any */
import { StateCreator } from 'zustand';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { createAppStore } from '@marriott/mi-store-utils';

type PendingFormDataStatusType = { [key: string]: boolean } | null;

type ConfirmationModalGenericPropsType = {
  optInConfirmationModalHeader: string;
  modalProceedButtonLabel: string;
  modalCancelButtonLabel: string;
};

type AlertComponentConfigType = {
  alertType?: 'apiError' | 'profileNotFound' | 'authIneligible' | 'profileUpdated' | 'authProfileUpdated';
  showAlertComponent: boolean;
};

export type LoggedInUserPrivacyRightsData = {
  doNotSellOptIn: boolean;
  doNotShareOptIn: boolean;
};

type PrivacyRightsState = {
  doNotSellShareFormsInputFieldsPendingDataStatuses: PendingFormDataStatusType[] | null;
  confirmationModalGenericData: ConfirmationModalGenericPropsType | null;
  alertComponentConfig: AlertComponentConfigType;
  userPrivacyRightsData: LoggedInUserPrivacyRightsData | null;
};

type PrivacyRightsStateActions = {
  setDoNotSellShareFormsInputFieldsPendingDataStatus: (pendingFormStatus: PendingFormDataStatusType) => void;
  isFormHasPendingChange: () => boolean;
  setConfirmationModalGenericData: (modalData: ConfirmationModalGenericPropsType) => void;
  setAlertComponentConfig: (alertComponentData: AlertComponentConfigType) => void;
  setLoggedInUserPrivacyRightsData: (userPrivacyRightsData: LoggedInUserPrivacyRightsData) => void;
};

const initialState: PrivacyRightsState = {
  doNotSellShareFormsInputFieldsPendingDataStatuses: null,
  confirmationModalGenericData: null,
  alertComponentConfig: {
    alertType: 'apiError',
    showAlertComponent: false,
  },
  userPrivacyRightsData: null,
};

export const privacyRightsStore: StateCreator<PrivacyRightsState & PrivacyRightsStateActions> = set => {
  return {
    ...initialState,

    setDoNotSellShareFormsInputFieldsPendingDataStatus: (pendingFormStatus: PendingFormDataStatusType) => {
      if (!pendingFormStatus) {
        set(prevState => ({
          ...prevState,
          doNotSellShareFormsInputFieldsPendingDataStatuses: null,
        }));
        return;
      }

      set(prevState => {
        const existingFormStatuses = prevState.doNotSellShareFormsInputFieldsPendingDataStatuses || [];
        const uniqueId = Object.keys(pendingFormStatus)[0];

        const foundIndex = existingFormStatuses.findIndex(ele => Object.prototype.hasOwnProperty.call(ele, uniqueId));

        return {
          ...prevState,
          doNotSellShareFormsInputFieldsPendingDataStatuses:
            foundIndex !== -1
              ? existingFormStatuses.map((ele, index) => (index === foundIndex ? pendingFormStatus : ele))
              : [...existingFormStatuses, pendingFormStatus],
        };
      });
    },

    isFormHasPendingChange: () => {
      const state = usePrivacyRightsStore.getState();
      return (
        state.doNotSellShareFormsInputFieldsPendingDataStatuses?.some(
          formStatus => formStatus && Object.values(formStatus)[0]
        ) ?? false
      );
    },

    setConfirmationModalGenericData: (modalData: ConfirmationModalGenericPropsType | null) => {
      set(prevState => {
        return {
          ...prevState,
          confirmationModalGenericData: modalData,
        };
      });
    },

    setAlertComponentConfig: (alertComponentData: AlertComponentConfigType) => {
      set(prevState => {
        return {
          ...prevState,
          alertComponentConfig: alertComponentData,
        };
      });
    },

    setLoggedInUserPrivacyRightsData: (userPrivacyRightsData: LoggedInUserPrivacyRightsData) => {
      set(prevState => {
        return {
          ...prevState,
          userPrivacyRightsData,
        };
      });
    },
  };
};

export const usePrivacyRightsStore = createAppStore(privacyRightsStore);
